import { ancestorsTransformer } from './ancestor.ts';
import { CONTEXT_PATH } from '../AdvancedSearch-constants.ts';
import type { useResultSupplierAdvancedSearchCQLQuery as AdvancedSearchCQLQuery } from '../__types__/useResultSupplierAdvancedSearchCQLQuery.ts';
import type {
	SearchResultType,
	useResultSupplierAdvancedAGGSearchQuery_search_SearchQueryAPI_search_SearchItemConnection_edges_SearchResultItemEdge_node_SearchConfluencePageBlogAttachment_confluenceEntity_ConfluencePage as AdvancedAGGSearchQueryNodeConfluenceEntity,
	useResultSupplierAdvancedAGGSearchQuery_search_SearchQueryAPI_search_SearchItemConnection_edges_SearchResultItemEdge_node_SearchConfluencePageBlogAttachment_space_SearchConfluenceResultSpace as AdvancedAGGSearchQueryNodeSpace,
	useResultSupplierAdvancedAGGSearchQuery as AdvancedAGGSearchQuery,
} from '../__types__/useResultSupplierAdvancedAGGSearchQuery.ts';
import type { AdvancedSearchResultType } from '../AdvancedSearch-types.ts';

export const cqlDataMapper = (
	cqlData: AdvancedSearchCQLQuery | undefined,
): AdvancedSearchResultType[] => {
	const nodes = cqlData?.search?.nodes || [];

	return nodes.map((node) => {
		const baseAncestor = node?.content?.space
			? {
					id: node.content.space.id || '',
					title: node.content.space.name || '',
					href: node.content.space.links?.webUi || '',
				}
			: null;

		return {
			entityType: node?.entityType || '',
			title: node?.title || '',
			excerpt: node?.excerpt || '',
			url: node?.url || '',
			lastModified: node?.lastModified || '',
			content: {
				id: node?.content?.id || '',
				type: (node?.content?.type || '').toLowerCase(),
				space: {
					id: node?.content?.space?.id || '',
					key: node?.content?.space?.key || '',
					name: node?.content?.space?.name || '',
					links: {
						webui: node?.content?.space?.links?.webUi || '',
					},
				},
			},
			space: {
				id: node?.space?.id || '',
				icon: {
					path: node?.space?.icon?.path || '',
				},
				webui: node?.url || '',
			},
			ancestors: ancestorsTransformer(
				(node?.content?.ancestors || []).map((a) => a ?? null),
				baseAncestor,
			),
		};
	});
};

// Need to only pass url path
export const parseAggUrl = (url: string): string => {
	try {
		return url.replace(`${new URL(url).origin + CONTEXT_PATH}`, '');
	} catch {
		return url;
	}
};

interface AdvancedAGGSearchQueryNode {
	id: string;
	entityId?: string | null;
	title: string;
	type: SearchResultType;
	url: string;
	lastModified?: Date | null;
	isVerified?: boolean;
	excerpt?: string | null;
	key?: string | null;
	iconPath?: string | null;
	webUiLink?: string | null;
	confluenceEntity?: AdvancedAGGSearchQueryNodeConfluenceEntity | null;
	space?: AdvancedAGGSearchQueryNodeSpace | null;
}

export const aggDataMapper = (
	aggData: AdvancedAGGSearchQuery | undefined,
): AdvancedSearchResultType[] => {
	const edges = aggData?.search?.search?.edges || [];

	return edges.map((edge) => {
		const node = edge.node as AdvancedAGGSearchQueryNode | null;
		const baseAncestor = node?.space
			? {
					id: node.space.spaceId || '',
					title: node.space.name || '',
					href: node.space.webUiLink || '',
				}
			: null;

		return {
			entityType: node?.type || '',
			subtype: node?.confluenceEntity?.subtype || '',
			title: node?.title || '',
			excerpt: node?.excerpt || '',
			url: parseAggUrl(node?.url || ''),
			lastModified: node?.lastModified?.toString() || '',
			isVerified: node?.isVerified || false,
			content: {
				id: node?.entityId || '',
				type: (node?.type || '').toLowerCase(),
				space: {
					id: node?.space?.spaceId || '',
					key: node?.space?.key || '',
					name: node?.space?.name || '',
					links: {
						webui: node?.space?.webUiLink || '',
					},
				},
			},
			space: {
				id: node?.entityId || '',
				icon: {
					path: node?.iconPath || '',
				},
				webui: node?.webUiLink || '',
			},
			ancestors: ancestorsTransformer(
				(node?.confluenceEntity?.ancestors || []).map((a) => a ?? null),
				baseAncestor,
			),
			emoji: node?.confluenceEntity?.metadata?.titleEmojiPublished?.value || '',
		};
	});
};
